import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  ErrorState,
  LoadingState,
  Page,
  Tabs,
} from "@marekl/adminer/components";
import { AppTopbar } from "../../../shell/AppTopbar";
import { useRuleSet, type Discipline } from "../../../lib/queries/rules";
import { RULESET_STATUS_LABELS, RULESET_STATUS_VARIANT } from "../../../lib/rulesLabels";
import { AgeTablesTab } from "./AgeTablesTab";
import { ClassTablesTab } from "./ClassTablesTab";
import { DeductionTablesTab } from "./DeductionTablesTab";
import { DisciplinesTab } from "./DisciplinesTab";
import { FloorsTab } from "./FloorsTab";
import { RecordingsTab } from "./RecordingsTab";
import { FormationTablesTab } from "./FormationTablesTab";
import { ScoringSheetsTab } from "./ScoringSheetsTab";
import { SettingsTab } from "./SettingsTab";
import { readsRuleSetsOnly, useMe } from "../../../auth/queries";
import { ROLE_LABEL } from "../../../lib/i18n/roles";
import type { ID } from "../../../lib/id";

const TABS = [
  { id: "disciplines", label: "Disciplíny" },
  { id: "age", label: "Věkové kategorie" },
  { id: "formations", label: "Formace" },
  { id: "classes", label: "Výkonnostní třídy" },
  { id: "deductions", label: "Srážkové tabulky" },
  { id: "scoring", label: "Hodnoticí tabulky" },
  { id: "floors", label: "Soutěžní plocha" },
  { id: "recordings", label: "Hudba" },
  { id: "settings", label: "Nastavení" },
];

/**
 * Which competitions locked the set, phrased as the server phrases it.
 *
 * Kept in step with httperr.structureLockedMessage by hand — the banner
 * and the 409 are the same sentence reaching the admin through two
 * channels, and e2e/specs/pravidla/ruleset.spec.ts asserts both.
 */
function lockedByText(by: { id: ID; name: string }[]): string {
  if (by.length === 1) return `Zamyká ji soutěž ${by[0].name}.`;
  if (by.length === 2) return `Zamykají ji soutěže ${by[0].name} a ${by[1].name}.`;
  return `Zamyká ji soutěž ${by[0].name} a další ${by.length - 1}.`;
}

export function RuleSetDetailPage() {
  const { data: me } = useMe();
  // Čtení soutěží: the whole set shown, as read-only as an archived one.
  const viewOnly = readsRuleSetsOnly(me);
  const { id } = useParams<{ id: string }>();
  const ruleSetId = id ?? "";
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get("tab"))
    ? (searchParams.get("tab") as string)
    : TABS[0].id;

  const { data, isLoading, error, refetch } = useRuleSet(ruleSetId);

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={[{ label: "Sady pravidel", href: "/admin/pravidla" }, { label: "Sada" }]} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <AppTopbar crumbs={[{ label: "Sady pravidel", href: "/admin/pravidla" }, { label: "Sada" }]} />
        <Page>
          <Page.Content>
            <ErrorState title="Sadu pravidel se nepodařilo načíst" error={error} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  const ruleSet = data.ruleset;
  // An archived set is frozen: the backend refuses every write, so the
  // UI hides the controls rather than offering actions that will 409.
  const archived = ruleSet.status === "archived";
  const readOnly = archived || viewOnly;
  // A set in service is a narrower freeze than archived: WHICH combinations
  // exist is settled because clubs are already looking at an offer built
  // from it, while the values inside them stay editable (a wrong time limit
  // must still be fixable mid-season, and Modul 6 re-judges music on every
  // read for exactly that reason). Mirrors rules.requireStructureEditable —
  // the server refuses either way; this stops the page offering the button.
  const structureLocked = data.in_service && !readOnly;
  // Which tables a discipline actually builds its matrix on. Only those
  // lose their rung deletes: a ladder nobody references holds no
  // combinations, so removing a rung of it drops nothing and the server
  // allows it — the same reason the table's own „Smazat" stays.
  const lockedTables = (pick: (d: Discipline) => ID) =>
    new Set(structureLocked ? data.disciplines.map(pick) : []);

  return (
    <>
      <AppTopbar
        crumbs={[{ label: "Sady pravidel", href: "/admin/pravidla" }, { label: ruleSet.name }]}
      />
      <Page>
        <Page.Header
          title={ruleSet.name}
          subtitle={ruleSet.description || undefined}
          actions={
            <Badge variant={RULESET_STATUS_VARIANT[ruleSet.status]}>
              {RULESET_STATUS_LABELS[ruleSet.status]}
            </Badge>
          }
        />
        <Page.Content stack gap="lg">
          {viewOnly && (
            <Alert variant="info" title="Pouze pro čtení">
              Máte roli {ROLE_LABEL.competitions_viewer}: sadu vidíte celou, ale nic v ní neměníte.
            </Alert>
          )}
          {archived && !viewOnly && (
            <Alert variant="warn">
              Sada je archivovaná a jen pro čtení. Chcete-li ji upravit, vraťte ji mezi
              rozpracované v záložce „Nastavení“; tím se ale změní pravidla, podle kterých se soutěžilo.
            </Alert>
          )}

          {structureLocked && (
            <Alert variant="warn" title="Sada je v ostrém provozu">
              {/* Which soutěž, not just „a published one": a set claimed by
                  one competition the admin could still un-publish is a
                  different situation from one claimed by five that have run.
                  One name plus a count rather than the whole list — a set
                  that ran a season is claimed by every round of it, and six
                  names is a wall nobody reads. Two get named in full;
                  „a další 1" is not a Czech sentence. Same wording as the
                  server's 409 (httperr.structureLockedMessage). */}
              {lockedByText(data.locked_by)}{" "}
              Strukturu už měnit nelze: disciplíny, věkové kategorie, formace
              a výkonnostní třídy zůstávají tak, jak je týmy viděly. Hodnoty upravovat
              můžete dál, tedy časové limity, srážkové a hodnoticí tabulky, plochy
              i nahrávky. Potřebujete-li jinou strukturu, sadu duplikujte a novou
              přiřaďte příštím soutěžím.
            </Alert>
          )}

          <Tabs
            tabs={TABS}
            value={tab}
            onChange={(next) => setSearchParams({ tab: next }, { replace: true })}
            aria-label="Části sady pravidel"
          />

          {tab === "disciplines" && (
            <DisciplinesTab ruleSetId={ruleSetId} readOnly={readOnly} structureLocked={structureLocked} workspace={data} />
          )}
          {tab === "age" && (
            <AgeTablesTab
              ruleSetId={ruleSetId}
              readOnly={readOnly}
              lockedTableIds={lockedTables((d) => d.age_category_table_id)}
              tables={data.age_tables}
            />
          )}
          {tab === "formations" && (
            <FormationTablesTab
              ruleSetId={ruleSetId}
              readOnly={readOnly}
              lockedTableIds={lockedTables((d) => d.formation_table_id)}
              tables={data.formation_tables}
            />
          )}
          {tab === "classes" && (
            <ClassTablesTab
              ruleSetId={ruleSetId}
              readOnly={readOnly}
              lockedTableIds={lockedTables((d) => d.performance_class_table_id)}
              tables={data.performance_class_tables}
            />
          )}
          {tab === "deductions" && (
            <DeductionTablesTab
              ruleSetId={ruleSetId}
              readOnly={readOnly}
              tables={data.deduction_tables}
            />
          )}
          {tab === "scoring" && (
            <ScoringSheetsTab
              ruleSetId={ruleSetId}
              readOnly={readOnly}
              sheets={data.scoring_sheets}
            />
          )}
          {tab === "floors" && (
            <FloorsTab ruleSetId={ruleSetId} readOnly={readOnly} floors={data.floors} />
          )}
          {tab === "recordings" && (
            <RecordingsTab
              ruleSetId={ruleSetId}
              readOnly={readOnly}
              recordings={data.recordings}
              minBitrateKbps={ruleSet.music_min_bitrate_kbps}
              minSampleRateHz={ruleSet.music_min_sample_rate_hz}
            />
          )}
          {tab === "settings" && <SettingsTab ruleSet={ruleSet} viewOnly={viewOnly} />}
        </Page.Content>
      </Page>
    </>
  );
}
